import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BuyerProcurementContactsQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const BUYER_PROCUREMENT_CONTACTS_QUERY = graphql(`
  query buyerProcurementContacts($buyerGuid: String!, $limit: Int) {
    buyerProcurementContacts(buyerGuid: $buyerGuid, limit: $limit) {
      contacts {
        name
        lastReferenced
        emails {
          value
          lastReferenced
        }
        phoneNumbers {
          value
          lastReferenced
        }
      }
    }
  }
`);

export type ProcurementContactFromQuery =
  BuyerProcurementContactsQuery["buyerProcurementContacts"]["contacts"][number];

/**
 * Procurement contacts scraped out of a buyer's notices — not the Cognism decision-makers
 * (`useContacts`). Keyed on guid; the Rails endpoint this replaced took a numeric id.
 *
 * `limit` is unset by default, matching both the resolver and the Rails endpoint: neither caps, so
 * defaulting here would truncate the largest buyer (3,314 contacts) with nothing in the table to say
 * so. Callers may still pass one to bound the response.
 *
 * The key is omitted rather than sent as `undefined`: the resolver's argument is `.optional()`, not
 * `.nullable()`, so a `null` limit is rejected as a bad request.
 */
export function useBuyerContacts(
  buyerGuid: string,
  options?: UseQueryOptions<
    BuyerProcurementContactsQuery,
    unknown,
    BuyerProcurementContactsQuery,
    QueryKey
  >,
  limit?: number,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["buyerProcurementContacts", buyerGuid, limit ?? "all"],
    BUYER_PROCUREMENT_CONTACTS_QUERY,
    [{ buyerGuid, ...(limit !== undefined && { limit }) }],
    { ...REACT_QUERY_OPTIONS_NEVER_REFETCH, ...options },
  );

  return { data: data?.buyerProcurementContacts.contacts, ...rest };
}
